import { useEffect, useRef } from 'react';
import { api } from './api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const CLAIMED_KEY = 'jixxeo_install_rewarded';

function isStandalone() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.matchMedia?.('(display-mode: minimal-ui)').matches ||
    window.matchMedia?.('(display-mode: window-controls-overlay)').matches ||
    window.navigator?.standalone === true
  );
}

/**
 * Fires POST /api/rewards/install-claim exactly once when:
 *  - the `appinstalled` event fires, OR
 *  - the app is already running in standalone mode and we haven't claimed yet.
 * Mounts once at app shell level.
 */
export function useInstallReward() {
  const { user, refresh } = useAuth();
  const triedRef = useRef(false);

  useEffect(() => {
    if (!user) return;
    if (triedRef.current) return;

    const alreadyClaimed = (() => {
      try { return localStorage.getItem(CLAIMED_KEY) === '1'; } catch { return false; }
    })();

    const claim = async (fromEvent) => {
      if (triedRef.current) return;
      triedRef.current = true;
      try {
        const { data } = await api.post('/rewards/install-claim');
        try { localStorage.setItem(CLAIMED_KEY, '1'); } catch {}
        if (data?.claimed && data?.amount) {
          toast.success(`🎉 +${data.amount} JXP install bonus unlocked!`, {
            description: 'Welcome to Jixxeo AI on your device.',
            duration: 6000,
          });
          refresh?.();
        }
      } catch {
        // Allow retry on next mount if server call fails
        triedRef.current = false;
      }
    };

    // Case 1: launched already as installed app
    if (isStandalone() && !alreadyClaimed) {
      claim('standalone');
    }

    // Case 2: live install event
    const onInstalled = () => { claim('appinstalled'); };
    window.addEventListener('appinstalled', onInstalled);
    return () => window.removeEventListener('appinstalled', onInstalled);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);
}
